import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PercentIcon from "@mui/icons-material/Percent";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CommentIcon from "@mui/icons-material/Comment";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import Swal from "sweetalert2";
import { LoaderScreen } from "../../../components/LoaderScreen";
import { FormatMoney } from "../../../../Helpers/FormatNumber";
import request from "../../../../Helpers/API";

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// Evita que se escriba manualmente un % fuera de 0-100 (min/max del input no lo impiden al teclear).
const clampPct = (value) => {
    if (value === "") return value;
    const n = Number(value);
    if (isNaN(n)) return value;
    return String(Math.min(100, Math.max(0, n)));
};

// Evita un monto condonado negativo o mayor al interés disponible.
const clampMonto = (value, maxMonto) => {
    if (value === "") return value;
    const n = Number(value);
    if (isNaN(n)) return value;
    return String(round2(Math.min(maxMonto, Math.max(0, n))));
};

// % correspondiente a un monto condonado, dado el total de interés disponible.
const montoAPorcentaje = (monto, totalInteres) => {
    if (monto === "" || !totalInteres) return "";
    return String(round2((Number(monto) / totalInteres) * 100));
};

// Alta y aprobación de condonación de interés — misma pantalla en ambos casos (según exista
// `folio`). El % de normal/moratorio se captura una sola vez y se aplica al total de intereses de
// TODAS las facturas seleccionadas (no por factura). El monto condonado se calcula solo a partir
// del %. Conectado a CondonacionIntereses/GuardarCondonacion, ObtenerLimiteUsuario,
// ObtenerCondonacionPorFolio y AutorizarCondonacion (mdl_Guarda_Condonacion_Interes,
// mdl_Limite_Condonacion_Usuario, mdl_Condonacion_Por_Folio, mdl_Autorizar_Condonacion).
export const CondonacionInteresAgregar = ({ folio, onCancelar, onGuardado }) => {
    const history = useHistory();
    const { state } = useLocation();
    const esAprobacion = Boolean(folio);

    const [cargandoDetalle, setCargandoDetalle] = useState(esAprobacion);
    const [detalle, setDetalle] = useState(null);
    const [facturasAprobacion, setFacturasAprobacion] = useState([]);

    const facturasCaptura = state?.facturasSeleccionadas || [];

    const totalInteresNormal = useMemo(
        () => (esAprobacion ? (detalle?.inormal_saldo || 0) : facturasCaptura.reduce((acc, f) => acc + (f.interespactado || 0), 0)),
        [esAprobacion, detalle, state?.facturasSeleccionadas]
    );
    const totalInteresMoratorio = useMemo(
        () => (esAprobacion ? (detalle?.imoratorio_saldo || 0) : facturasCaptura.reduce((acc, f) => acc + (f.interesmoratorio || 0), 0)),
        [esAprobacion, detalle, state?.facturasSeleccionadas]
    );

    const [limiteUsuario, setLimiteUsuario] = useState(null);
    const [aplicaNormal, setAplicaNormal] = useState(false);
    const [aplicaMoratorio, setAplicaMoratorio] = useState(false);
    const [pctNormal, setPctNormal] = useState("");
    const [pctMoratorio, setPctMoratorio] = useState("");
    // Texto del input de monto condonado — estado propio (no derivado del %) para que el usuario
    // pueda borrar/editar libremente mientras escribe, sin que cada dígito se recalcule desde el %
    // ya redondeado y "salte" el valor mostrado.
    const [montoNormalTexto, setMontoNormalTexto] = useState("");
    const [montoMoratorioTexto, setMontoMoratorioTexto] = useState("");
    const [comentarios, setComentarios] = useState("");
    const [guardando, setGuardando] = useState(false);

    // % máximo del usuario logueado — se usa tanto en captura (para saber si excede y queda
    // pendiente) como en aprobación (para saber si quien está autorizando tiene límite suficiente).
    useEffect(() => {
        request
            .post("CondonacionIntereses/ObtenerLimiteUsuario", {})
            .then(setLimiteUsuario)
            .catch(() => setLimiteUsuario({ porcentaje_normal: 0, porcentaje_moratorio: 0 }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Modo aprobación: trae el detalle de la condonación pendiente por folio.
    useEffect(() => {
        if (!esAprobacion) return;
        setCargandoDetalle(true);
        request
            .post(`CondonacionIntereses/ObtenerCondonacionPorFolio?folio=${folio}`, {})
            .then((result) => {
                const item = result?.condonacion;
                if (!item) {
                    setCargandoDetalle(false);
                    Swal.fire("CONDONACIÓN DE INTERÉS", "No se encontró la condonación solicitada.", "error");
                    return;
                }
                setDetalle(item);
                setFacturasAprobacion(Array.isArray(result?.facturas) ? result.facturas : []);
                setAplicaNormal((item.inormal_porcentaje || 0) > 0);
                setAplicaMoratorio((item.imoratorio_porcentaje || 0) > 0);
                setPctNormal(item.inormal_porcentaje > 0 ? String(item.inormal_porcentaje) : "");
                setPctMoratorio(item.imoratorio_porcentaje > 0 ? String(item.imoratorio_porcentaje) : "");
                setMontoNormalTexto(
                    item.inormal_porcentaje > 0 ? String(round2(((item.inormal_saldo || 0) * item.inormal_porcentaje) / 100)) : ""
                );
                setMontoMoratorioTexto(
                    item.imoratorio_porcentaje > 0 ? String(round2(((item.imoratorio_saldo || 0) * item.imoratorio_porcentaje) / 100)) : ""
                );
                setComentarios(item.comentarios || "");
                setCargandoDetalle(false);
            })
            .catch(() => {
                setCargandoDetalle(false);
                Swal.fire("CONDONACIÓN DE INTERÉS", "No se pudo cargar la condonación a autorizar.", "error");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folio]);

    const montoCondonadoNormal = aplicaNormal && pctNormal !== "" ? round2((totalInteresNormal * Number(pctNormal)) / 100) : 0;
    const montoCondonadoMoratorio = aplicaMoratorio && pctMoratorio !== "" ? round2((totalInteresMoratorio * Number(pctMoratorio)) / 100) : 0;
    const saldoRestanteNormal = round2(totalInteresNormal - montoCondonadoNormal);
    const saldoRestanteMoratorio = round2(totalInteresMoratorio - montoCondonadoMoratorio);

    // PENDIENTE DE CONFIRMAR: edición inversa (capturar el monto y calcular el % automáticamente).
    // Lógica lista para activarse — el % seguiría siendo la fuente de verdad para lo que se
    // guarda; el monto usa su propio estado de texto para poder editarlo libremente (borrar,
    // escribir) sin que el valor derivado del % lo sobrescriba en cada tecla, y se sincroniza con
    // el % en ambos sentidos. Deshabilitado por ahora: los inputs de monto están comentados en el
    // JSX más abajo, junto con montoNormalTexto/montoMoratorioTexto.
    // const handlePctNormalChange = (value) => {
    //     const pct = clampPct(value);
    //     setPctNormal(pct);
    //     setMontoNormalTexto(pct === "" ? "" : String(round2((totalInteresNormal * Number(pct)) / 100)));
    // };
    // const handlePctMoratorioChange = (value) => {
    //     const pct = clampPct(value);
    //     setPctMoratorio(pct);
    //     setMontoMoratorioTexto(pct === "" ? "" : String(round2((totalInteresMoratorio * Number(pct)) / 100)));
    // };
    // const handleMontoNormalChange = (value) => {
    //     const monto = clampMonto(value, totalInteresNormal);
    //     setMontoNormalTexto(monto);
    //     setPctNormal(montoAPorcentaje(monto, totalInteresNormal));
    // };
    // const handleMontoMoratorioChange = (value) => {
    //     const monto = clampMonto(value, totalInteresMoratorio);
    //     setMontoMoratorioTexto(monto);
    //     setPctMoratorio(montoAPorcentaje(monto, totalInteresMoratorio));
    // };

    const excedeLimite =
        !esAprobacion &&
        limiteUsuario &&
        ((aplicaNormal && Number(pctNormal || 0) > limiteUsuario.porcentaje_normal) ||
            (aplicaMoratorio && Number(pctMoratorio || 0) > limiteUsuario.porcentaje_moratorio));

    // En aprobación, solo se muestra el tipo de interés que sí fue solicitado (% > 0 al guardar).
    // En captura, no tiene sentido ofrecer un % a condonar sobre un interés que es 0.
    const mostrarNormal = esAprobacion ? (detalle?.inormal_porcentaje || 0) > 0 : totalInteresNormal > 0;
    const mostrarMoratorio = esAprobacion ? (detalle?.imoratorio_porcentaje || 0) > 0 : totalInteresMoratorio > 0;

    // Ya autorizada/rechazada: solo consulta, sin botones de acción ni edición de %.
    // ObtenerCondonacionPorFolio devuelve el código corto ('P'/'A'/'R'), no el texto largo.
    const yaResuelta = esAprobacion && detalle?.estatus !== "P";

    // Quien está autorizando debe tener límite suficiente para el % solicitado en el folio;
    // si no alcanza, no puede modificar el % ni aprobar (solo puede rechazar).
    const sinLimiteSuficiente =
        esAprobacion &&
        !yaResuelta &&
        limiteUsuario &&
        (((detalle?.inormal_porcentaje || 0) > limiteUsuario.porcentaje_normal) ||
            ((detalle?.imoratorio_porcentaje || 0) > limiteUsuario.porcentaje_moratorio));

    const soloLectura = yaResuelta || sinLimiteSuficiente;

    const validar = () => {
        if (!aplicaNormal && !aplicaMoratorio) {
            Swal.fire("CONDONACIÓN DE INTERÉS", "Activa al menos un tipo de interés a condonar.", "warning");
            return false;
        }
        if (aplicaNormal && (pctNormal === "" || isNaN(Number(pctNormal)) || Number(pctNormal) < 0 || Number(pctNormal) > 100)) {
            Swal.fire("CONDONACIÓN DE INTERÉS", "El porcentaje de interés normal debe estar entre 0 y 100.", "warning");
            return false;
        }
        if (aplicaMoratorio && (pctMoratorio === "" || isNaN(Number(pctMoratorio)) || Number(pctMoratorio) < 0 || Number(pctMoratorio) > 100)) {
            Swal.fire("CONDONACIÓN DE INTERÉS", "El porcentaje de interés moratorio debe estar entre 0 y 100.", "warning");
            return false;
        }
        if (!comentarios.trim()) {
            Swal.fire("CONDONACIÓN DE INTERÉS", "Captura un comentario que justifique la condonación.", "warning");
            return false;
        }
        return true;
    };

    const handleGuardar = () => {
        if (!validar()) return;

        const post = {
            idcliente: state.idcliente,
            Saldo: totalInteresNormal + totalInteresMoratorio,
            Inormal_saldo: totalInteresNormal,
            Inormal_porcentaje: aplicaNormal ? Number(pctNormal) : 0,
            Inormal_descuento: montoCondonadoNormal,
            Inormal_pagado: saldoRestanteNormal,
            Imoratorio_saldo: totalInteresMoratorio,
            Imoratorio_porcentaje: aplicaMoratorio ? Number(pctMoratorio) : 0,
            Imoratorio_descuento: montoCondonadoMoratorio,
            Imoratorio_pagado: saldoRestanteMoratorio,
            Comentarios: comentarios.trim(),
            facturas: JSON.stringify(
                facturasCaptura.map((f) => ({
                    Documento: f.documento,
                    Serie: f.serie,
                    Saldo: f.saldo,
                    Inormal: f.interespactado,
                    Imoratorio: f.interesmoratorio,
                }))
            ),
        };

        setGuardando(true);
        request
            .post("CondonacionIntereses/GuardarCondonacion", post)
            .then((result) => {
                setGuardando(false);
                const condonacion = Array.isArray(result) ? result[0] : result;
                const quedoPendiente = Boolean(condonacion?.usuarios_notificar);
                Swal.fire(
                    "CONDONACIÓN DE INTERÉS",
                    quedoPendiente
                        ? "El porcentaje capturado excede tu límite autorizado — se envió la solicitud de aprobación de la condonación."
                        : `Folio ${condonacion?.folio} guardado y aplicado.`,
                    "success"
                );
                onGuardado();
            })
            .catch(() => {
                setGuardando(false);
                Swal.fire("CONDONACIÓN DE INTERÉS", "No se pudo guardar la condonación. Intenta nuevamente.", "error");
            });
    };

    const handleAutorizar = (aprobado) => {
        if (aprobado && !validar()) return;

        const post = {
            Folio: folio,
            aprobado,
            Inormal_porcentaje: aplicaNormal ? Number(pctNormal) : 0,
            Imoratorio_porcentaje: aplicaMoratorio ? Number(pctMoratorio) : 0,
            ComentarioAutoriza: "",
        };

        setGuardando(true);
        request
            .post("CondonacionIntereses/AutorizarCondonacion", post)
            .then(() => {
                setGuardando(false);
                Swal.fire(
                    "CONDONACIÓN DE INTERÉS",
                    `Folio ${folio} ${aprobado ? "aprobado" : "rechazado"} correctamente.`,
                    "success"
                );
                onGuardado ? onGuardado() : history.goBack();
            })
            .catch(() => {
                setGuardando(false);
                Swal.fire("CONDONACIÓN DE INTERÉS", "No se pudo registrar la autorización. Intenta nuevamente.", "error");
            });
    };

    if (cargandoDetalle) {
        return <LoaderScreen loader={{ mostrar: true, alerta: false, error: false, mensaje: "" }} />;
    }

    return (
        <div className="crm-main-screen">
            <LoaderScreen loader={{ mostrar: guardando, alerta: false, error: false, mensaje: "" }} />
            <div className="crm-body">
                <div className="crm-page">
                    <div className="crm-module">
                        <div className="crm-module-head">
                            <div className="crm-head-grid">
                                <div className="crm-head-left">
                                    <button className="crm-btn-regresar" onClick={() => (onCancelar ? onCancelar() : history.goBack())}>
                                        <ArrowBackIcon /> Regresar
                                    </button>
                                </div>
                                <div className="crm-head-center">
                                    <h1 className="crm-module-title">{detalle?.razon_social || state?.razonsocial}</h1>
                                    <p className="crm-module-subtitle">
                                        {esAprobacion
                                            ? `AUTORIZAR CONDONACIÓN${detalle?.estatus === "A" ? ` — FOLIO ${folio}` : ""}`
                                            : "NUEVA CONDONACIÓN DE INTERÉS"}
                                    </p>
                                </div>
                                <div className="crm-head-right" />
                            </div>
                            <div className="crm-head-divider">
                                <div className="crm-head-divider-dot" />
                            </div>
                        </div>

                        <div className="mjr-wrapper cia-wrapper-condonacion">
                            <div className="cdi-panel cia-panel-condonacion">
                                <div className="cdi-panel-header">
                                    <div className="cdi-panel-header-icono"><PercentIcon /></div>
                                    <h3 className="cdi-panel-titulo">Porcentaje a condonar</h3>
                                    {limiteUsuario && (
                                        <span className="cdi-panel-badge">
                                            Tu límite: {limiteUsuario.porcentaje_normal}% normal / {limiteUsuario.porcentaje_moratorio}% moratorio
                                        </span>
                                    )}
                                </div>

                                <div className="cia-resumen-encabezados">
                                    <span>Tipo</span>
                                    <span>Interés</span>
                                    <span>% a condonar</span>
                                    <span>Condonado</span>
                                    <span>Interés a pagar</span>
                                </div>

                                <div className="cia-resumen">
                                    {mostrarNormal && (
                                        <div className="cia-resumen-fila">
                                            <label className="cdi-switch-row">
                                                <span className="cdi-switch">
                                                    <input type="checkbox" checked={aplicaNormal} disabled={soloLectura} onChange={(e) => setAplicaNormal(e.target.checked)} />
                                                    <span className="cdi-switch-slider" />
                                                </span>
                                                Normales
                                            </label>
                                            <span className="cia-resumen-monto">{FormatMoney(totalInteresNormal)}</span>
                                            <div className="cdi-input-pct cia-input-pct">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={100}
                                                    step="0.01"
                                                    disabled={!aplicaNormal || soloLectura}
                                                    value={pctNormal}
                                                    onChange={(e) => setPctNormal(clampPct(e.target.value))}
                                                    placeholder="0"
                                                />
                                                <span>%</span>
                                            </div>
                                            {/* PENDIENTE DE CONFIRMAR: edición inversa (capturar monto y calcular % automáticamente).
                                                Lógica ya lista en handleMontoNormalChange/montoNormalTexto — pendiente activar este input.
                                            <div className="cdi-input-pct cia-input-monto">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={totalInteresNormal}
                                                    step="0.01"
                                                    disabled={!aplicaNormal || soloLectura}
                                                    value={montoNormalTexto}
                                                    onChange={(e) => handleMontoNormalChange(e.target.value)}
                                                    placeholder="0"
                                                />
                                            </div> */}
                                            <span className="cia-resumen-condonado">{FormatMoney(montoCondonadoNormal)}</span>
                                            <span className="cia-resumen-restante">{FormatMoney(saldoRestanteNormal)}</span>
                                        </div>
                                    )}

                                    {mostrarMoratorio && (
                                        <div className="cia-resumen-fila">
                                            <label className="cdi-switch-row">
                                                <span className="cdi-switch">
                                                    <input type="checkbox" checked={aplicaMoratorio} disabled={soloLectura} onChange={(e) => setAplicaMoratorio(e.target.checked)} />
                                                    <span className="cdi-switch-slider" />
                                                </span>
                                                Moratorios
                                            </label>
                                            <span className="cia-resumen-monto">{FormatMoney(totalInteresMoratorio)}</span>
                                            <div className="cdi-input-pct cia-input-pct">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={100}
                                                    step="0.01"
                                                    disabled={!aplicaMoratorio || soloLectura}
                                                    value={pctMoratorio}
                                                    onChange={(e) => setPctMoratorio(clampPct(e.target.value))}
                                                    placeholder="0"
                                                />
                                                <span>%</span>
                                            </div>
                                            {/* PENDIENTE DE CONFIRMAR: edición inversa (capturar monto y calcular % automáticamente).
                                                Lógica ya lista en handleMontoMoratorioChange/montoMoratorioTexto — pendiente activar este input.
                                            <div className="cdi-input-pct cia-input-monto">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={totalInteresMoratorio}
                                                    step="0.01"
                                                    disabled={!aplicaMoratorio || soloLectura}
                                                    value={montoMoratorioTexto}
                                                    onChange={(e) => handleMontoMoratorioChange(e.target.value)}
                                                    placeholder="0"
                                                />
                                            </div> */}
                                            <span className="cia-resumen-condonado">{FormatMoney(montoCondonadoMoratorio)}</span>
                                            <span className="cia-resumen-restante">{FormatMoney(saldoRestanteMoratorio)}</span>
                                        </div>
                                    )}

                                    <div className="cia-resumen-fila cia-resumen-total">
                                        <span>Total interés</span>
                                        <span className="cia-resumen-monto">{FormatMoney(totalInteresNormal + totalInteresMoratorio)}</span>
                                        <span />
                                        <span className="cia-resumen-condonado">{FormatMoney(montoCondonadoNormal + montoCondonadoMoratorio)}</span>
                                        <span className="cia-resumen-restante">{FormatMoney(saldoRestanteNormal + saldoRestanteMoratorio)}</span>
                                    </div>
                                </div>

                                {excedeLimite && (
                                    <div className="cia-alerta-limite">
                                        El porcentaje capturado excede tu límite autorizado — esta condonación quedará en estatus
                                        "Pendiente Autorización".
                                    </div>
                                )}

                                <div className="cia-comentarios-header">
                                    <div className="cdi-panel-header-icono"><CommentIcon /></div>
                                    <h3 className="cdi-panel-titulo">Comentarios{esAprobacion ? " del solicitante" : ""}</h3>
                                </div>

                                <textarea
                                    className="cia-comentarios"
                                    rows={3}
                                    maxLength={500}
                                    placeholder="Describe el motivo de la condonación..."
                                    value={comentarios}
                                    onChange={(e) => setComentarios(e.target.value)}
                                    disabled={esAprobacion}
                                />

                                {sinLimiteSuficiente && (
                                    <div className="cia-alerta-limite">
                                        Tu porcentaje autorizado no alcanza para este folio. Solo puede autorizarlo o rechazarlo alguien con límite suficiente.
                                    </div>
                                )}

                                <div className="cia-footer">
                                    {esAprobacion ? (
                                        soloLectura ? (
                                            yaResuelta && (
                                                <span className={`cdi-badge ${detalle?.estatus === "A" ? "cdi-badge-on" : "cdi-badge-rechazada"}`}>
                                                    {detalle?.estatus === "A" ? "Autorizada" : "Rechazada"}
                                                </span>
                                            )
                                        ) : (
                                            <>
                                                <button className="cdi-btn-cancelar cia-btn-rechazar" onClick={() => handleAutorizar(false)} disabled={guardando}>
                                                    <HighlightOffIcon /> Rechazar
                                                </button>
                                                <button className="cdi-btn-guardar" onClick={() => handleAutorizar(true)} disabled={guardando}>
                                                    <CheckCircleOutlineIcon /> {guardando ? "Guardando..." : "Aprobar"}
                                                </button>
                                            </>
                                        )
                                    ) : (
                                        <>
                                            <button className="cdi-btn-cancelar" onClick={() => (onCancelar ? onCancelar() : history.goBack())}>
                                                Cancelar
                                            </button>
                                            <button className="cdi-btn-guardar" onClick={handleGuardar} disabled={guardando}>
                                                {guardando ? "Guardando..." : "Guardar"}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="cdi-panel">
                                <div className="cdi-panel-header">
                                    <div className="cdi-panel-header-icono"><ReceiptLongIcon /></div>
                                    <h3 className="cdi-panel-titulo">Facturas seleccionadas</h3>
                                    <span className="cdi-panel-badge">{(esAprobacion ? facturasAprobacion : facturasCaptura).length} facturas</span>
                                </div>

                                {esAprobacion ? (
                                    <div className="cdi-tabla-wrap scroll">
                                        <table className="cdi-tabla" cellPadding={0} cellSpacing={0}>
                                            <thead>
                                                <tr>
                                                    <th><div>Serie</div></th>
                                                    <th><div className="borde-izq">No. Factura</div></th>
                                                    <th><div className="borde-izq">Saldo</div></th>
                                                    <th><div className="borde-izq">Int. Pactado</div></th>
                                                    <th><div className="borde-izq">Int. Moratorio</div></th>
                                                    <th><div className="borde-izq">Total</div></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {facturasAprobacion.map((f) => (
                                                    <tr key={f.id}>
                                                        <td>{f.serie}</td>
                                                        <td className="cdi-tabla-usuario">{f.documento}</td>
                                                        <td className="cia-celda-num">{FormatMoney(f.saldo)}</td>
                                                        <td className="cia-celda-num">{FormatMoney(f.inormal)}</td>
                                                        <td className="cia-celda-num">{FormatMoney(f.imoratorio)}</td>
                                                        <td className="cdi-tabla-pct cia-celda-num">{FormatMoney((f.saldo || 0) + (f.inormal || 0) + (f.imoratorio || 0))}</td>
                                                    </tr>
                                                ))}
                                                {facturasAprobacion.length === 0 && (
                                                    <tr>
                                                        <td colSpan={6} className="cdi-tabla-vacio">No hay facturas relacionadas.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="cdi-tabla-wrap scroll">
                                        <table className="cdi-tabla" cellPadding={0} cellSpacing={0}>
                                            <thead>
                                                <tr>
                                                    <th><div>Serie</div></th>
                                                    <th><div className="borde-izq">No. Factura</div></th>
                                                    <th><div className="borde-izq">Saldo</div></th>
                                                    <th><div className="borde-izq">Int. Pactado</div></th>
                                                    <th><div className="borde-izq">Int. Moratorio</div></th>
                                                    <th><div className="borde-izq">Total</div></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {facturasCaptura.map((f) => (
                                                    <tr key={f.idfactura}>
                                                        <td>{f.serie}</td>
                                                        <td className="cdi-tabla-usuario">{f.documento}</td>
                                                        <td className="cia-celda-num">{FormatMoney(f.saldo)}</td>
                                                        <td className="cia-celda-num">{FormatMoney(f.interespactado)}</td>
                                                        <td className="cia-celda-num">{FormatMoney(f.interesmoratorio)}</td>
                                                        <td className="cdi-tabla-pct cia-celda-num">{FormatMoney(f.total)}</td>
                                                    </tr>
                                                ))}
                                                {facturasCaptura.length === 0 && (
                                                    <tr>
                                                        <td colSpan={6} className="cdi-tabla-vacio">
                                                            No hay facturas seleccionadas.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CondonacionInteresAgregar;
